// Multi-slide release announcement modal. Designed for occasional broadcasts
// when we ship a meaningful set of features. Triggered by the SysMessageGate
// when the backend returns a recognized "whats_new_*" code.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  Image as ImageIcon,
  ShieldCheck,
  Gavel,
  Download,
  ArrowRight,
  CreditCard,
  Crown,
  Layers,
  Activity,
} from "lucide-react";

const RELEASE_LABEL = "Release · June 2026";

// Slide config. Add or remove slides to change the announcement contents.
// `image` is a fully-qualified URL (Cloudflare Images delivery in this app).
const SLIDES = [
  {
    key: "payment-fraud",
    eyebrow: "New module",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/6e318f57-e734-4a44-a700-bf38ae88d700/w=1600",
    title: (
      <>
        Introducing{" "}
        <span className="text-sky-300">Payment Fraud Intelligence</span>
      </>
    ),
    description: (
      <>
        Compromised-card intelligence for fraud teams. Browse exposed cards
        by issuer or BIN, run a card-information explorer, and set BIN or
        issuer watchlists that page you when new exposures land — from a
        dedicated{" "}
        <span className="text-sky-200 font-medium">Payment Fraud</span> page.
      </>
    ),
    bullets: [
      (
        <>
          <span className="text-sky-200 font-medium">Full card metadata</span>{" "}
          — BIN-6, BIN-8, last 4, issuer bank, country, network, type, tier,
          and expiry
        </>
      ),
      (
        <>
          <span className="text-sky-200 font-medium">Watchlist alerts</span>{" "}
          on BIN ranges or specific issuers, with per-rule history and email
          notifications
        </>
      ),
      "Issuer leaderboard, BIN lookup, and a server-paginated card explorer",
      "Validity recomputed at query time so analysts see current exposure, not stale data",
      (
        <>
          Available as an{" "}
          <span className="text-sky-200 font-medium">add-on</span>{" "}
          for Enterprise and Threat Intelligence — included in the new
          Extended Threat Intelligence license
        </>
      ),
    ],
  },
  {
    key: "supplier-security",
    eyebrow: "New module",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/50485c97-73a7-49f2-5599-5bdf4bd75700/w=1600",
    title: (
      <>
        Introducing{" "}
        <span className="text-sky-300">Supplier Security</span>
      </>
    ),
    description: (
      <>
        Continuous credential-exposure scoring for the vendors and partners
        your organization depends on. Add a supplier by domain and get a
        per-vendor risk grade, source-typed indices, and a live credential
        feed — all from a dedicated{" "}
        <span className="text-sky-200 font-medium">Supplier Security</span>{" "}
        page.
      </>
    ),
    bullets: [
      (
        <>
          <span className="text-sky-200 font-medium">A–F risk grade</span> per
          supplier, driven by malware, combolist, and breach exposure with a
          composite score and delta-vs-prior
        </>
      ),
      (
        <>
          <span className="text-sky-200 font-medium">Live sessions surface</span>{" "}
          — MFA-bypassing cookies on critical hosts get called out front-and-center
        </>
      ),
      "Inline trailing-18-month sparklines per source so trend direction is visible at a glance",
      (
        <>
          A built-in{" "}
          <span className="text-sky-200 font-medium">Credentials tab</span>{" "}
          drops you straight into the corporate credential records for that
          vendor — same drill-down as Global Search
        </>
      ),
      (
        <>
          Available on{" "}
          <span className="text-sky-200 font-medium">Enterprise (64)</span>,{" "}
          Threat Intelligence (128), and Extended TI (256 suppliers)
        </>
      ),
    ],
  },
  {
    key: "incident-details-cookies",
    eyebrow: "Enhanced",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/3353e185-e7e6-434b-0091-4a4927957600/w=1600",
    title: (
      <>
        Inline cookies & downloads in{" "}
        <span className="text-sky-300">Incident Details</span>
      </>
    ),
    description: (
      <>
        When session cookies were captured alongside a credential, the
        incident detail sidebar now surfaces them inline. Click{" "}
        <span className="text-sky-200 font-medium">See</span> next to a
        credential and the full cookie set — names, values, domains,
        expiry, and flags — expands right inside the credential card. A
        new{" "}
        <span className="text-sky-200 font-medium">Downloads</span> tab
        also appears when a full archive is available, so analysts can
        pull the raw artifact straight from the incident.
      </>
    ),
    bullets: [
      (
        <>
          <span className="text-sky-200 font-medium">Per-cookie detail</span>{" "}
          — name, value, domain, path, expiry, Secure / HttpOnly / SameSite
          flags, and source browser
        </>
      ),
      "Filter cookies by name, value, or domain when a session yields dozens of keys",
      "Copy individual cookies or export the full set as JSON for Burp, curl, or browser-import workflows",
      (
        <>
          New{" "}
          <span className="text-sky-200 font-medium">Downloads</span> tab —
          surfaces the full archive when available, so the raw artifact is
          one click away
        </>
      ),
      (
        <>
          Latest credentials sort to the top under a{" "}
          <span className="text-sky-200 font-medium">From latest log</span>{" "}
          header so the freshest exposure is unmistakable
        </>
      ),
    ],
  },
  {
    key: "extended-ti",
    eyebrow: "New license",
    // No image — uses the 3-card recap grid to summarize what's bundled.
    title: (
      <>
        Top-tier{" "}
        <span className="text-sky-300">Extended Threat Intelligence</span>
      </>
    ),
    description: (
      <>
        A new license that bundles every add-on and raises quotas across the
        board. Built for teams that want the full WhiteIntel surface without
        negotiating add-ons separately.
      </>
    ),
    bullets: [
      (
        <>
          <span className="text-sky-200 font-medium">$25,000 USD / year</span>{" "}
          — billed annually only
        </>
      ),
      "Resale and customer-facing product rights included",
      "Priority developer support tier",
    ],
    cards: [
      {
        name: "All add-ons bundled",
        tag: "Zero add-on cost",
        description:
          "Threat Feed API, Payment Fraud, Investigation+, and Managed Takedowns.",
        icon: Layers,
      },
      {
        name: "500 calls / day · every API",
        tag: "Leak · Threat Feed · Payment Fraud",
        description:
          "Same daily quota across all three APIs, no separate negotiation.",
        icon: Activity,
      },
      {
        name: "Higher monitoring limits",
        tag: "Watchlists · Seats · Takedowns",
        description:
          "256 watchlist tokens · 64 dark-web keywords · 64 GitHub repos · 5 seats · 20 takedowns / year.",
        icon: ShieldCheck,
      },
    ],
  },
  {
    key: "ready-to-deploy",
    eyebrow: "Ready to deploy?",
    // No image — replaced by a recap grid + CTAs below the description.
    title: (
      <>
        Put it to work for{" "}
        <span className="text-sky-300">your team</span>
      </>
    ),
    description: (
      <>
        Payment Fraud Intelligence is available as an{" "}
        <span className="text-sky-200 font-medium">add-on</span>{" "}
        for Enterprise and Threat Intelligence — or pick{" "}
        <span className="text-sky-200 font-medium">
          Extended Threat Intelligence
        </span>{" "}
        to get it bundled with every other add-on and higher quotas across
        the board.
      </>
    ),
    cards: [
      {
        name: "Payment Fraud Intelligence",
        tag: "New add-on",
        description:
          "Compromised-card monitoring with watchlist alerts and a card explorer.",
        icon: CreditCard,
      },
      {
        name: "Extended Threat Intelligence",
        tag: "New license",
        description:
          "All add-ons bundled, higher quotas, resale rights — for teams that want the full surface.",
        icon: Crown,
      },
      {
        name: "Existing add-ons",
        tag: "Still available",
        description:
          "Threat Feed API, Investigation+, and Managed Takedowns — sold separately or bundled in Extended TI.",
        icon: Sparkles,
      },
    ],
    primaryCta: { label: "View plans", href: "/upgrades" },
    secondaryCta: { label: "Talk to sales", href: "/contact" },
  },
];

export default function WhatsNewModal({
  open,
  onClose,
  enterMs = 420,
  exitMs = 260,
  easing = "cubic-bezier(0.16, 1, 0.3, 1)",
}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(open);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [imgError, setImgError] = useState({}); // { [slideKey]: true }
  const total = SLIDES.length;
  const slide = SLIDES[step];
  const isFirst = step === 0;
  const isLast = step === total - 1;

  // Mount-time entrance animation (same pattern as FrostedModal — start hidden,
  // double-RAF, then transition to shown so the entrance actually animates).
  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
    } else {
      setShow(false);
    }
  }, [open]);

  const backdropTiming = useMemo(
    () => ({
      transitionDuration: `${show ? 220 : exitMs}ms`,
      transitionTimingFunction: easing,
    }),
    [show, exitMs, easing]
  );
  const panelTiming = useMemo(
    () => ({
      transitionDuration: `${show ? enterMs : exitMs}ms`,
      transitionTimingFunction: easing,
      transitionDelay: show ? "60ms" : "0ms",
    }),
    [show, enterMs, exitMs, easing]
  );

  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (!show) setVisible(false);
  };

  // ESC + arrow keys
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight" && !isLast) setStep((s) => s + 1);
      else if (e.key === "ArrowLeft" && !isFirst) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose, isFirst, isLast]);

  // Per-slide content fade so the swap reads as deliberate, not a reflow.
  const [contentShow, setContentShow] = useState(true);
  const prevStepRef = useRef(step);
  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    setContentShow(false);
    const t = setTimeout(() => setContentShow(true), 60);
    return () => clearTimeout(t);
  }, [step]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="What's new"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/75 backdrop-blur-sm transition-opacity ${
          show ? "opacity-100" : "opacity-0"
        }`}
        style={backdropTiming}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        onTransitionEnd={handleTransitionEnd}
        className={`relative z-10 w-full max-w-4xl overflow-hidden rounded-md border border-sky-500/20
                    bg-[#0a1224]
                    shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85)]
                    ring-1 ring-white/[0.03]
                    transition-all will-change-transform will-change-opacity
                    ${
                      show
                        ? "opacity-100 translate-y-0 scale-100"
                        : "opacity-0 translate-y-6 scale-[0.97]"
                    }`}
        style={panelTiming}
      >
        {/* Top accent stripe */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent z-20" />

        {/* Close — generous hit area, sits above the body via z-30 */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-30 rounded-md p-2 text-neutral-400 hover:text-white hover:bg-white/[0.08] transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Body — text leads. */}
        <div
          className={`relative px-8 pt-7 transition-all duration-300 ${
            contentShow ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
          }`}
        >
          {/* Right padding reserves space for the absolute-positioned close
              button so the "step / total" counter doesn't get tucked under it. */}
          <div className="flex items-center justify-between gap-3 mb-3 pr-12">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold min-w-0">
              <Sparkles className="h-3 w-3 text-sky-300/80 shrink-0" />
              <span className="truncate">{RELEASE_LABEL}</span>
              <span className="text-neutral-600 shrink-0">·</span>
              <span className="text-neutral-400 normal-case tracking-normal font-normal truncate">
                {slide.eyebrow}
              </span>
            </div>
            <div className="text-[11px] text-neutral-500 font-medium tabular-nums shrink-0">
              {step + 1} / {total}
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-white leading-tight tracking-tight">
            {slide.title}
          </h2>

          <p className="mt-3 text-[14px] leading-relaxed text-neutral-300/95">
            {slide.description}
          </p>

          {slide.bullets && slide.bullets.length > 0 && (
            <ul className="mt-4 space-y-2 text-[13px] text-neutral-300">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Visual area below the text. Slides ship either an image OR a card
            grid (the recap slide uses cards). Card grid is the more enterprise
            way to summarize multiple features without needing a separate
            graphic. */}
        {slide.cards && slide.cards.length > 0 ? (
          <div
            className={`mx-8 mt-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 transition-opacity duration-300 ${
              contentShow ? "opacity-100" : "opacity-0"
            }`}
          >
            {slide.cards.map((card, i) => {
              const CardIcon = card.icon;
              return (
                <div
                  key={i}
                  className="rounded-lg border border-white/[0.08] bg-gradient-to-br from-sky-500/[0.04] via-[#0e1830] to-[#0a1224] p-4 hover:border-sky-500/30 transition-colors"
                >
                  {CardIcon && (
                    <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-sky-500/30 bg-sky-500/10">
                      <CardIcon className="h-4 w-4 text-sky-300" />
                    </div>
                  )}
                  <div className="text-sm font-semibold text-white leading-tight">
                    {card.name}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-sky-300/70 font-semibold">
                    {card.tag}
                  </div>
                  {card.description && (
                    <p className="mt-2 text-[12px] leading-snug text-neutral-400">
                      {card.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={`mx-6 mt-6 mb-6 rounded-lg border border-white/[0.06] bg-gradient-to-br from-sky-500/[0.04] via-[#0e1830] to-[#0a1224] overflow-hidden transition-opacity duration-300 ${
              contentShow ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Image box height — generous so landscape screenshots
                render close to native size rather than being scaled
                down. Reads at h-[22rem] (352px) on mobile, h-[28rem]
                (448px) on md+. Combined with the wider max-w-4xl panel
                this gives screenshots ~3.5x the previous footprint. */}
            <div className="relative h-[22rem] md:h-[28rem]">
              {!imgError[slide.key] ? (
                <picture>
                  {/* Mobile-optimized asset under md (768px). Falls back to the
                      desktop URL when the slide hasn't shipped a mobile version
                      yet, so partial coverage doesn't break the layout. */}
                  <source
                    media="(max-width: 767px)"
                    srcSet={slide.imageMobile || slide.image}
                  />
                  <img
                    src={slide.image}
                    alt=""
                    loading={step === 0 ? "eager" : "lazy"}
                    onError={() =>
                      setImgError((prev) => ({ ...prev, [slide.key]: true }))
                    }
                    className="h-full w-full object-contain"
                  />
                </picture>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-neutral-600">
                  <ImageIcon className="h-8 w-8" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer / nav */}
        <div className="border-t border-white/[0.06] bg-black/20 px-8 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={isFirst}
            className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 px-3 py-1.5 text-sm font-medium text-neutral-300 hover:text-white hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>

          <div className="flex items-center gap-1.5">
            {SLIDES.map((s, i) => {
              const active = i === step;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    active
                      ? "w-8 bg-sky-400"
                      : "w-1.5 bg-neutral-600 hover:bg-neutral-400"
                  }`}
                />
              );
            })}
          </div>

          {!isLast ? (
            <button
              onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : slide.primaryCta ? (
            // Recap slide: explicit Close + secondary "Talk to sales" +
            // primary "View plans". Close gives users a way out without
            // navigating, so the modal doesn't feel like a forced funnel.
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-neutral-400 hover:text-white hover:bg-white/[0.04] transition-colors"
              >
                Close
              </button>
              {slide.secondaryCta && (
                <button
                  type="button"
                  onClick={() => {
                    onClose?.();
                    navigate(slide.secondaryCta.href);
                  }}
                  className="rounded-md border border-sky-900/40 px-3 py-1.5 text-sm font-medium text-neutral-300 hover:text-white hover:bg-white/[0.04] hover:border-sky-900/60 transition-colors"
                >
                  {slide.secondaryCta.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  navigate(slide.primaryCta.href);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
              >
                {slide.primaryCta.label}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            >
              Get Started
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
